/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useState, useEffect } from "react";
import {
  Image,
  Dimensions,
  Platform,
  TouchableOpacity,
  View,
} from "react-native";
import LinearGradient from "react-native-web-linear-gradient";
import Icon from "react-icons-kit";
import { trash } from "react-icons-kit/fa/trash";
import { clone } from "react-icons-kit/fa/clone";
import { pencil } from "react-icons-kit/fa/pencil";

import { useSelector } from "react-redux";
import { RootState } from "store/types";

import { SummitStatusCard, Text } from "UpduoLibrary";
import {
  skaleBlack,
  skaleDeactiveGray,
  skaleYellow,
  skaleLightGray,
  skaleWhite,
  skaleWebTextGray,
  colorWithOpacity,
  skaleRed,
} from "config/UpduoColors";

import {
  allowedMatchAlgorithmList,
  formattedSummitDateRangeString,
  getResizedImageUrl,
} from "config/Utilities";
import * as UpduoAnalytics from "config/UpduoAnalytics";
import summitPlaceholderImg from "assets/gradients/upduo-colors-04.svg";
import { SummitType } from "graphql/internal/generated";

function SummitDeleteButton({ disabled, onPress }) {
  const [isHoveringDelete, setIsHoveringDelete] = useState<boolean>(false);

  const buttonColor = isHoveringDelete ? skaleRed : skaleWhite;
  return (
    <TouchableOpacity
      style={{
        paddingLeft: 10,
        alignItems: "center",
        justifyContent: "center",
      }}
      disabled={disabled}
      onPress={onPress}
      {...Platform.select({
        web: {
          onMouseEnter: () => {
            setIsHoveringDelete(true);
          },
          onMouseLeave: () => {
            setIsHoveringDelete(false);
          },
        },
      })}
    >
      <div
        style={{
          color: disabled ? skaleLightGray : buttonColor,
          paddingBottom: 2.5,
        }}
      >
        <Icon size={15} icon={trash} />
      </div>
    </TouchableOpacity>
  );
}

interface SummitEntryProps {
  summit: SummitType;
  canEdit: boolean;
  onPress: () => void;
  onPressClone: (summitId: string) => void;
  editSummit: (summitId: string) => void;
  showEditModal: (summitId: string) => void;
  showDeleteModal: (summitName: string, summitId: string) => void;
  unassignedUserCount: number;
  unassignedUserproxyCount: number;
}

export default function SummitEntry({
  summit,
  canEdit,
  onPress,
  onPressClone,
  editSummit,
  showEditModal,
  showDeleteModal,
  unassignedUserCount,
  unassignedUserproxyCount,
}: SummitEntryProps) {
  const selfObject = useSelector((state: RootState) => state.selfObject);
  const internalPermission =
    selfObject.primaryEmail?.endsWith("@upduo.com") ||
    selfObject.primaryEmail?.endsWith("@arcade.co");

  const startDateObject = new Date(summit ? Date.parse(summit.startDate) : "");
  const endDateObject = new Date(summit ? Date.parse(summit.endDate) : "");

  const now = new Date();
  const summitIsLive = now >= startDateObject && now < endDateObject;
  const summitHasEnded = now > endDateObject;

  const disabled = false;

  const [isHoveringClone, setIsHoveringClone] = useState(false);
  const [isHoveringEdit, setIsHoveringEdit] = useState(false);

  const [windowWidth, setWindowWidth] = useState(
    Dimensions.get("window").width
  );

  const _handleWindowResize = () => {
    setWindowWidth(Dimensions.get("window").width);
  };

  useEffect(() => {
    window.addEventListener("resize", _handleWindowResize);
    return () => {
      window.removeEventListener("resize", _handleWindowResize);
    };
  }, []);

  const cloneBtnColor = isHoveringClone ? skaleYellow : skaleWhite;
  const summitCloneButton = (
    <TouchableOpacity
      style={{
        paddingLeft: 10,
      }}
      {...Platform.select({
        web: {
          onMouseEnter: () => {
            setIsHoveringClone(true);
          },
          onMouseLeave: () => {
            setIsHoveringClone(false);
          },
        },
      })}
      onPress={() => {
        UpduoAnalytics.logEvent("user-summit-new", {
          isClone: true,
          cloneId: summit.id,
        });
        onPressClone(summit.id);
      }}
    >
      <div
        style={{
          color: disabled ? skaleLightGray : cloneBtnColor,
          paddingBottom: 2.5,
        }}
      >
        <Icon size={15} icon={clone} />
      </div>
    </TouchableOpacity>
  );

  const editBtnColor = isHoveringEdit ? skaleYellow : skaleWhite;
  const summitEditButton = (
    <TouchableOpacity
      style={{
        paddingLeft: 10,
        alignItems: "center",
        justifyContent: "center",
      }}
      {...Platform.select({
        web: {
          onMouseEnter: () => {
            setIsHoveringEdit(true);
          },
          onMouseLeave: () => {
            setIsHoveringEdit(false);
          },
        },
      })}
      onPress={() => {
        UpduoAnalytics.logEvent("user-summit-edit", {
          isEdit: true,
          editId: summit.id,
        });
        !summitHasEnded ? editSummit(summit.id) : showEditModal(summit.id);
      }}
    >
      <div
        style={{
          color: disabled ? skaleLightGray : editBtnColor,
          paddingBottom: 2.5,
        }}
      >
        <Icon size={15} icon={pencil} />
      </div>
    </TouchableOpacity>
  );

  return (
    <TouchableOpacity
      style={{
        flexGrow: 1,
        width: summitHasEnded ? "25vw" : "80vw",
        minWidth: summitHasEnded ? 300 : 600,
        height: summitHasEnded ? 180 : 270,
        marginRight: summitHasEnded ? 10 : 0,
        marginVertical: 10,
      }}
      onPress={() => {
        onPress();
      }}
    >
      <View
        style={{
          flex: 1,
        }}
      >
        <Image
          style={{
            flex: 1,
            borderRadius: 18,
            opacity: summitHasEnded ? 0.3 : 1,
            height: summitHasEnded ? 180 : 270,
            minWidth: summitHasEnded ? 300 : 600,
          }}
          source={{
            uri: summit.imageUrl
              ? getResizedImageUrl(
                  summit.imageUrl,
                  0,
                  summitHasEnded ? 180 : 270
                )
              : summitPlaceholderImg,
          }}
        />
        <LinearGradient
          colors={
            summitHasEnded
              ? [
                  colorWithOpacity(skaleBlack, 0.5),
                  colorWithOpacity(skaleBlack, 0.5),
                ]
              : [
                  colorWithOpacity(skaleBlack, 0.1),
                  colorWithOpacity(skaleBlack, 0.5),
                ]
          }
          style={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            minWidth: summitHasEnded ? 300 : 600,
            height: summitHasEnded ? 180 : 270,
            borderRadius: 18,
          }}
        />
        <View
          style={{
            position: "absolute",
            bottom: summitHasEnded ? 20 : 25,
            left: 20,
            right: 0,
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          <View
            style={{
              justifyContent: "flex-end",
            }}
          >
            <View
              style={{
                flexDirection: "row",
              }}
            >
              <SummitStatusCard
                summitHasEnded={summitHasEnded}
                summitIsLive={summitIsLive}
              />
              <View
                style={{
                  backgroundColor: skaleDeactiveGray,
                  borderRadius: 4,
                  alignSelf: "center",
                  justifyContent: "center",
                  marginHorizontal: 6,
                  height: 23,
                }}
              >
                <Text
                  isHeading={true}
                  style={{
                    paddingHorizontal: 12,
                    color: summit.isPublished ? skaleWebTextGray : skaleWhite,
                    fontSize: 12,
                    textTransform: "uppercase",
                  }}
                >
                  {summit.isPublished ? "Published" : "Unpublished"}
                </Text>
              </View>
              {internalPermission ? (
                <View
                  style={{
                    borderColor: skaleWhite,
                    borderWidth: 1,
                    borderRadius: 4,
                    alignSelf: "center",
                    justifyContent: "center",
                    marginLeft: 3,
                    height: 23,
                  }}
                >
                  <Text
                    style={{
                      paddingHorizontal: 12,
                      color: skaleWhite,
                      fontSize: 12,
                      textTransform: "uppercase",
                    }}
                  >{`${
                    allowedMatchAlgorithmList.find(
                      (algorithm) => algorithm.id === summit.matchAlgorithm
                    )?.name
                  }`}</Text>
                </View>
              ) : null}
            </View>
            <View
              style={{
                paddingTop: 10,
                width: summitHasEnded ? "15vw" : "70vw",
                minWidth: (summitHasEnded ? 300 : 600) - 20,
              }}
            >
              <Text
                isHeading={true}
                numberOfLines={2}
                style={{
                  fontSize: summitHasEnded ? 16 : 24,
                  color: skaleWhite,
                }}
              >
                {summit.name}
              </Text>
              <Text
                style={{
                  fontSize: summitHasEnded ? 14 : 16,
                  color: skaleWhite,
                }}
              >
                {formattedSummitDateRangeString(startDateObject, endDateObject)}
              </Text>
            </View>
          </View>
          <View
            style={{
              justifyContent: "flex-end",
            }}
          >
            {canEdit ? (
              <View
                style={{
                  flexDirection: "column",
                }}
              >
                <View
                  style={{
                    marginHorizontal: 20,
                    alignItems: "center",
                    flexDirection: windowWidth > 1360 ? "row" : "column",
                    justifyContent: "flex-end",
                  }}
                >
                  {summitEditButton}
                  {summitCloneButton}
                  <SummitDeleteButton
                    disabled={false}
                    onPress={() => {
                      UpduoAnalytics.logEvent("user-summit-delete", {
                        summitId: summit.id,
                      });
                      showDeleteModal(summit.name, summit.id);
                    }}
                  />
                </View>
              </View>
            ) : null}
          </View>
        </View>
      </View>
      {internalPermission ? (
        <View style={{ position: "absolute", top: 20, right: 20 }}>
          {summit?.cohorts && summit?.cohorts?.length > 0 ? (
            summit?.cohorts?.map((cohort, index) => (
              <Text
                key={index}
                style={{
                  color: skaleWhite,
                  fontSize: 12,
                  textTransform: "uppercase",
                }}
              >
                {`${cohort?.name} (${cohort?.userCount} members, ${cohort?.userproxyCount} not registered)`}
              </Text>
            ))
          ) : (
            <Text
              style={{
                color: skaleWhite,
                fontSize: 12,
                textTransform: "uppercase",
              }}
            >
              {`DEFAULT UNDEFINED COHORT (${unassignedUserCount} members, ${unassignedUserproxyCount} not registered)`}
            </Text>
          )}
        </View>
      ) : null}
    </TouchableOpacity>
  );
}
